import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useLNAuth } from '@shared/context/LNAuthContext';
import AppLayout from './components/AppLayout';

import { isNative } from '@shared/utils/platform';

// Where logged-out web visitors go: the marketing site. Per environment
// (.env*): production sends them to loudneighbors.com; staging and previews
// leave it empty, which falls back to this app's /login, so staging never
// hands people to the production site. The native apps always use /login.
const LANDING_URL = isNative ? null : (import.meta.env.VITE_LANDING_URL || null);

// Core nav pages — eagerly loaded to avoid flicker on navigation
import DashboardPage from './pages/DashboardPage';
import CoachellaPage from './pages/CoachellaPage';
import ProfilePage from './pages/ProfilePage';

// Secondary pages — lazy loaded
const LoginPage = lazy(() => import('./pages/LoginPage'));
const CallbackPage = lazy(() => import('./pages/CallbackPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const AnalysisPage = lazy(() => import('./pages/AnalysisPage'));
const ConcertsPage = lazy(() => import('./pages/ConcertsPage'));
const GroupsPage = lazy(() => import('./pages/GroupsPage'));
const GroupDetailPage = lazy(() => import('./pages/GroupDetailPage'));
const LeaderboardsPage = lazy(() => import('./pages/LeaderboardsPage'));
const ArtistPage = lazy(() => import('./pages/ArtistPage'));
const FullListPage = lazy(() => import('./pages/FullListPage'));

const LoadingFallback = () => {
  if (!isNative) return <div className="fixed inset-0 bg-[#0F0F0F]" />;
  return (
    <div className="fixed inset-0 bg-[#0F0F0F]">
      <img src="/splash.png" alt="Loud Neighbors" className="w-full h-full object-cover" />
    </div>
  );
};

function SplashOverlay() {
  const { isLoading } = useLNAuth();
  const [phase, setPhase] = useState('hold'); // hold → fade → gone
  useEffect(() => {
    if (!isLoading && phase === 'hold') {
      const t1 = setTimeout(() => setPhase('fade'), 200);
      const t2 = setTimeout(() => setPhase('gone'), 900);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [isLoading, phase]);
  if (!isNative) return null;
  if (phase === 'gone') return null;
  return (
    <div
      className="fixed inset-0 z-[9999] bg-[#0F0F0F] pointer-events-none transition-opacity duration-700 ease-out"
      style={{ opacity: phase === 'fade' ? 0 : 1 }}
    >
      <img src="/splash.png" alt="" className="w-full h-full object-cover" />
    </div>
  );
}

const ExternalRedirect = ({ to }) => {
  if (to) {
    window.location.href = to;
    return <LoadingFallback />;
  }
  // Native app, or no landing site in this environment: go to login
  return <Navigate to="/login" replace />;
};

const ProtectedRoute = ({ children }) => {
  const { user, isLoading } = useLNAuth();
  if (isLoading) return <LoadingFallback />;
  if (!user) return <ExternalRedirect to={LANDING_URL} />;
  if (!user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return children;
};

const OnboardingRoute = ({ children }) => {
  const { user, isLoading } = useLNAuth();
  if (isLoading) return <LoadingFallback />;
  if (!user) return <ExternalRedirect to={LANDING_URL} />;
  if (user.onboarding_completed) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  const { user, isLoading } = useLNAuth();

  return (
    <Suspense fallback={null}>
      <SplashOverlay />
      <Routes>
        {/* Public */}
        <Route
          path="/"
          element={
            isLoading ? (
              <LoadingFallback />
            ) : user ? (
              user.onboarding_completed ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/onboarding" replace />
              )
            ) : (
              <ExternalRedirect to={LANDING_URL} />
            )
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<CallbackPage />} />

        {/* Onboarding + Analysis */}
        <Route path="/onboarding" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />
        <Route path="/analysis" element={
          user && !isLoading ? <AnalysisPage /> : <Navigate to="/" replace />
        } />

        {/* Protected — single AppLayout wraps all authenticated routes */}
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/dashboard/all/:section" element={<FullListPage />} />
          <Route path="/concerts" element={<ConcertsPage />} />
          <Route path="/festival" element={
            user && !user.onboarding_completed ? <Navigate to="/onboarding" replace /> : <CoachellaPage />
          } />
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/groups/:id" element={<GroupDetailPage />} />
          <Route path="/leaderboards" element={<LeaderboardsPage />} />
          <Route path="/artist/:id" element={<ArtistPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/:username" element={<ProfilePage />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
